import { DayTimeDuration } from '@ais-ui/ui-core';

export function formatCustomDuration(
  duration: string | DayTimeDuration | undefined
): string {
  if (!duration) {
    return 'P0000-00-00T00:00:00';
  }

  // Handle new object format { days, hours, minutes }
  if (typeof duration === 'object' && 'days' in duration) {
    const paddedDays = String(duration.days).padStart(2, '0');
    const paddedHours = String(duration.hours).padStart(2, '0');
    const paddedMinutes = String(duration.minutes).padStart(2, '0');
    return `P0000-00-${paddedDays}T${paddedHours}:${paddedMinutes}:00`;
  }

  // Handle legacy string format: "5 10:00"
  const parts = duration.split(' ');
  if (parts.length !== 2) {
    throw new Error('Invalid duration format');
  }

  const [days, time] = parts;
  const [hours, minutes] = time.split(':').map(Number);

  if (isNaN(hours) || isNaN(minutes)) {
    throw new Error('Invalid time format');
  }

  // Zero-padding for hours, minutes, and seconds
  const paddedHours = String(hours).padStart(2, '0');
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedDays = String(days).padStart(2, '0');

  // Constructing the custom duration string
  return `P0000-00-${paddedDays}T${paddedHours}:${paddedMinutes}:00`;
}

export function parseCustomDuration(
  customDuration: string
): undefined | DayTimeDuration {
  // customDuration looks like P0000-00-05T10:00:00

  // Validate the input format
  const durationRegex = /^P0000-00-(\d{2})T(\d{2}):(\d{2}):00$/;
  const match = customDuration.match(durationRegex);

  if (!match) {
    return;
  }

  // Extract days, hours, and minutes
  const [, days, hours, minutes] = match;

  // Convert to numbers
  const numDays = Number(days);
  const numHours = Number(hours);
  const numMinutes = Number(minutes);

  // Validate extracted values
  if (isNaN(numDays) || isNaN(numHours) || isNaN(numMinutes)) {
    return;
  }

  return {
    days: numDays,
    hours: numHours,
    minutes: numMinutes,
  };
}

/**
 * @deprecated Use parseCustomDuration instead which returns DayTimeDuration object
 */
export function parseCustomDurationToString(
  customDuration: string
): undefined | string {
  const parsed = parseCustomDuration(customDuration);
  if (!parsed) return;

  const numHours = String(parsed.hours).padStart(2, '0');
  const numMinutes = String(parsed.minutes).padStart(2, '0');

  return `${parsed.days} ${numHours}:${numMinutes}`;
}

export function parseSecondsToCustomDuration(
  seconds: number
): undefined | string {
  if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0) {
    return;
  }

  const days = Math.floor(seconds / (3600 * 24));
  const hours = Math.floor((seconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  let result = '';

  if (days > 0) {
    result += `${days} day${days > 1 ? 's' : ''} `;
  }
  if (hours > 0) {
    result += `${hours} hour${hours > 1 ? 's' : ''} `;
  }
  if (minutes > 0 || (days === 0 && hours === 0)) {
    result += `${minutes} minute${minutes > 1 ? 's' : ''}`;
  }

  return result.trim();
}
